import type { ActiveEntitlements } from '../../lib/api';
import { canAccessExam, hasActiveEntitlement } from '@examflow/learning-space';

export interface AuthIntent {
  redirectTo: string;
  eyebrow: string;
  title: string;
  description: string;
  nextStepLabel: string;
}

const DEFAULT_REDIRECT = '/workspace';

const ACCOUNT_PATHS = new Set(['/login', '/register', '/forgot-password']);

const TRAINING_ACCESS_PATHS = [
  '/workspace',
  '/training',
  '/mock-exam',
  '/diagnosis/ability',
  '/quiz',
];

const routeIntents: Array<{
  prefix: string;
  intent: Omit<AuthIntent, 'redirectTo'>;
}> = [
  {
    prefix: '/training/session',
    intent: {
      eyebrow: '继续本轮训练',
      title: '专项训练做题页',
      description: '登录后回到当前训练 session，继续完成本轮题目。',
      nextStepLabel: '回到训练',
    },
  },
  {
    prefix: '/training',
    intent: {
      eyebrow: '进入训练配置',
      title: '专项训练入口',
      description: '登录后继续选择科目与难度，随时开练。',
      nextStepLabel: '开始练',
    },
  },
  {
    prefix: '/mock-exam',
    intent: {
      eyebrow: '进入模考',
      title: '模拟考试',
      description: '登录后继续进入平台模拟考试流程。',
      nextStepLabel: '进入模考',
    },
  },
  {
    prefix: '/diagnosis/ability',
    intent: {
      eyebrow: '查看诊断',
      title: '能力诊断',
      description: '登录后查看能力诊断、薄弱项和下一步训练建议。',
      nextStepLabel: '查看诊断',
    },
  },
  {
    prefix: '/quiz',
    intent: {
      eyebrow: '继续做题',
      title: '练习题目',
      description: '登录后回到当前题目练习流程。',
      nextStepLabel: '继续做题',
    },
  },
  {
    prefix: '/redeem',
    intent: {
      eyebrow: '开启权益',
      title: '兑换训练权限',
      description: '登录后绑定兑换码，开启对应考试训练包。',
      nextStepLabel: '去兑换',
    },
  },
  {
    prefix: '/profile',
    intent: {
      eyebrow: '进入个人中心',
      title: '个人资料与考试空间',
      description: '登录后查看已开通的考试空间和账号操作。',
      nextStepLabel: '查看个人中心',
    },
  },
  {
    prefix: '/workspace',
    intent: {
      eyebrow: '进入空间',
      title: 'TCF Canada 练习空间',
      description: '登录后进入工作台，查看今天该练什么和下一步任务。',
      nextStepLabel: '进入工作台',
    },
  },
  {
    prefix: '/',
    intent: {
      eyebrow: '回到入口',
      title: '语言考试训练空间',
      description: '登录后回到考试空间入口，继续选择训练目标。',
      nextStepLabel: '回到入口',
    },
  },
];

function isAllowedProductPath(pathname: string): boolean {
  return routeIntents.some(({ prefix }) =>
    prefix === '/' ? pathname === '/' : pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function isTrainingAccessPath(pathname: string): boolean {
  return TRAINING_ACCESS_PATHS.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function sanitizeAuthRedirect(rawRedirect?: string | null): string {
  const raw = rawRedirect?.trim();
  if (!raw || raw.startsWith('//') || /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(raw)) {
    return DEFAULT_REDIRECT;
  }

  let parsed: URL;
  try {
    parsed = new URL(raw, 'https://examflow.local');
  } catch {
    return DEFAULT_REDIRECT;
  }

  if (
    !raw.startsWith('/') ||
    ACCOUNT_PATHS.has(parsed.pathname) ||
    !isAllowedProductPath(parsed.pathname)
  ) {
    return DEFAULT_REDIRECT;
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function resolveAuthIntent(rawRedirect?: string | null): AuthIntent {
  const redirectTo = sanitizeAuthRedirect(rawRedirect);
  const pathname = new URL(redirectTo, 'https://examflow.local').pathname;
  const match =
    routeIntents.find(({ prefix }) =>
      prefix === '/' ? pathname === '/' : pathname === prefix || pathname.startsWith(`${prefix}/`)
    ) ?? routeIntents[routeIntents.length - 1];

  return {
    redirectTo,
    ...match.intent,
  };
}

export function buildRedeemRedirect(rawFinalRedirect?: string | null): string {
  const redirectTo = sanitizeAuthRedirect(rawFinalRedirect);
  return `/redeem?redirect=${encodeURIComponent(redirectTo)}`;
}

export function buildLoginRedirectForRedeem(rawFinalRedirect?: string | null): string {
  return `/login?redirect=${encodeURIComponent(buildRedeemRedirect(rawFinalRedirect))}`;
}

export function resolvePostAuthRedirect(
  rawRedirect: string | null | undefined,
  entitlements: ActiveEntitlements | null | undefined
): string {
  const redirectTo = sanitizeAuthRedirect(rawRedirect);
  const parsed = new URL(redirectTo, 'https://examflow.local');
  const pathname = parsed.pathname;

  if (pathname === '/redeem' || !isTrainingAccessPath(pathname)) {
    return redirectTo;
  }

  const routeExamType = parsed.searchParams.get('examType')?.trim().toLowerCase();
  const entitledForRoute = routeExamType
    ? canAccessExam(entitlements, routeExamType)
    : hasActiveEntitlement(entitlements);

  if (entitledForRoute) {
    return redirectTo;
  }

  return buildRedeemRedirect(redirectTo);
}
